import { useEffect } from "react";

/**
 * Closes a table cell's popover as soon as the page or any container
 * scrolls. The popover is portaled above all page chrome, so once its trigger
 * scrolls under the app header, the pinned columns or past the table's edge,
 * it would keep floating over them, detached from the row it belongs to.
 *
 * Scrolling inside a popover (its own content, or a table or list nested in
 * it) leaves it open.
 */
export function useCloseOnScroll(
  open: boolean,
  setOpen: (open: boolean) => void,
) {
  useEffect(() => {
    if (!open) return;

    // `scroll` doesn't bubble, so only a capture listener sees element
    // scrolls as well as the document's.
    const onScroll = (event: Event) => {
      if (
        event.target instanceof Element &&
        event.target.closest("[data-radix-popper-content-wrapper]")
      ) {
        return;
      }
      setOpen(false);
    };
    // Listen from the next frame on. Scroll events are dispatched at the start
    // of a frame, so one for a scroll that landed just before the popover
    // opened (bringing its trigger into view, or a scroll still settling) can
    // arrive after the click, and must not close it.
    const frame = requestAnimationFrame(() =>
      document.addEventListener("scroll", onScroll, {
        capture: true,
        passive: true,
      }),
    );
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener("scroll", onScroll, { capture: true });
    };
  }, [open, setOpen]);
}
